import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { ensureAuthenticated } from '../utils/auth';
import { getCoins, initializeProfile, addCoins, spendCoins } from '../utils/coinManager';
import { supabase } from '../utils/supabaseClient';
import { logAuthDebug } from '../utils/authDebugLogger';
import { useAutoCheckin } from '../utils/hooks/useAutoCheckin';
import { withTimeout } from '../utils/supabaseAuthSafe';
import { normalizeEnvKey } from '../utils/envKeyUtils';
import { validatePlayerName } from '../utils/playerNameValidation';

const ProfileContext = createContext();
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const POINTS_CACHE_PREFIX = 'zi-points-cache:v1';
const SUPABASE_REST_PROFILES_URL = (() => {
  const supabaseUrl = normalizeEnvKey(process.env.REACT_APP_SUPABASE_URL);
  return supabaseUrl ? `${supabaseUrl.replace(/\/+$/, '')}/rest/v1/profiles` : null;
})();
const SUPABASE_REST_API_KEY =
  normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY) ||
  normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY) ||
  null;

const buildPointsCacheKey = (userId) => `${POINTS_CACHE_PREFIX}:${userId}`;

const readCachedPoints = (userId) => {
  if (!userId) return null;
  try {
    const raw = window?.localStorage?.getItem(buildPointsCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      seasonPoints: Number.isFinite(parsed.seasonPoints) ? parsed.seasonPoints : 0,
      lifetimePoints: Number.isFinite(parsed.lifetimePoints) ? parsed.lifetimePoints : 0,
      checkinStreak: Number.isFinite(parsed.checkinStreak) ? parsed.checkinStreak : 0,
      previousSeason: parsed.previousSeason ?? null,
    };
  } catch {
    return null;
  }
};

const writeCachedPoints = (userId, payload) => {
  if (!userId) return;
  try {
    window?.localStorage?.setItem(
      buildPointsCacheKey(userId),
      JSON.stringify({
        seasonPoints: Number.isFinite(payload?.seasonPoints) ? payload.seasonPoints : 0,
        lifetimePoints: Number.isFinite(payload?.lifetimePoints) ? payload.lifetimePoints : 0,
        checkinStreak: Number.isFinite(payload?.checkinStreak) ? payload.checkinStreak : 0,
        previousSeason: payload?.previousSeason ?? null,
        updatedAt: Date.now(),
      })
    );
  } catch {
    // ignore
  }
};

export const ProfileProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [coins, setCoins] = useState(0);
  const [loading, setLoading] = useState(true);
  const [seasonPoints, setSeasonPoints] = useState(0);
  const [lifetimePoints, setLifetimePoints] = useState(0);
  const [checkinStreak, setCheckinStreak] = useState(0);
  const [previousSeason, setPreviousSeason] = useState(null);
  const [dailyCheckinReward, setDailyCheckinReward] = useState(null);
  const initSeqRef = useRef(0);
  const currentUserIdRef = useRef(null);

  // Keep a synchronous ref of auth state so auth event handlers can decide whether
  // to show "loading" UI without relying on potentially-stale closures.
  useEffect(() => {
    currentUserIdRef.current = user?.id || null;
  }, [user?.id]);

  const fetchProfileViaRest = useCallback(async (userId, accessToken) => {
    if (!userId || !accessToken || !SUPABASE_REST_PROFILES_URL) {
      return null;
    }

    logAuthDebug('fetch_profile_rest_start', {
      userId,
      hasApiKey: Boolean(SUPABASE_REST_API_KEY),
    });

    try {
      const url = new URL(SUPABASE_REST_PROFILES_URL);
      url.searchParams.set('select', '*');
      url.searchParams.set('id', `eq.${userId}`);

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };
      if (SUPABASE_REST_API_KEY) {
        headers.apikey = SUPABASE_REST_API_KEY;
      }

      const response = await withTimeout(
        fetch(url.toString(), { method: 'GET', headers }),
        6000,
        'profiles.rest.fetch'
      );

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        logAuthDebug('fetch_profile_rest_http_error', {
          userId,
          status: response.status,
          bodyText: bodyText ? String(bodyText).slice(0, 240) : '',
        });
        return null;
      }

      const rows = await withTimeout(response.json(), 3000, 'profiles.rest.json');
      const row = Array.isArray(rows) ? rows[0] || null : null;

      if (row) {
        logAuthDebug('fetch_profile_rest_success', {
          userId,
          username: row?.username || null,
          coins: row?.coins ?? null,
        });
      } else {
        logAuthDebug('fetch_profile_rest_empty', { userId });
      }

      return row;
    } catch (error) {
      logAuthDebug('fetch_profile_rest_error', {
        userId,
        message: error?.message || String(error),
      });
      return null;
    }
  }, []);

  // Function to fetch the complete profile data
  const fetchProfile = useCallback(async (userId, { accessTokenHint = null } = {}) => {
    logAuthDebug('fetch_profile_start', {
      userId,
      hasAccessTokenHint: Boolean(accessTokenHint),
    });

    // On auth events we already have a valid access token in-memory. Prefer direct
    // REST fetch first to avoid occasional supabase-js query hangs during refresh.
    if (accessTokenHint) {
      const restProfile = await fetchProfileViaRest(userId, accessTokenHint);
      if (restProfile) {
        return restProfile;
      }
      logAuthDebug('fetch_profile_rest_miss_fallback_supabase', { userId });
    }

    try {
      const { data, error } = await withTimeout(
        supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle(),
        6000,
        'profiles.select(*)'
      );
        
      if (error) {
        logAuthDebug('fetch_profile_error', {
          userId,
          code: error?.code || null,
          message: error?.message || String(error),
        });
        throw error;
      }
      
      if (data) {
        logAuthDebug('fetch_profile_success', {
          userId,
          username: data?.username || null,
          coins: data?.coins ?? null,
        });
        return data;
      }

      logAuthDebug('fetch_profile_empty', { userId });
      
      return null;
    } catch (error) {
      logAuthDebug('fetch_profile_catch', {
        userId,
        message: error?.message || String(error),
      });
      console.error('Error fetching profile:', error);
      return null;
    }
  }, [fetchProfileViaRest]);

  const fetchProfileWithRetry = useCallback(
    async (userId, { attempts = 3, baseDelayMs = 900, accessTokenHint = null } = {}) => {
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      const profileData = await fetchProfile(userId, { accessTokenHint });
      if (profileData) {
        if (attempt > 1) {
          logAuthDebug('fetch_profile_retry_success', { userId, attempt });
        }
        return profileData;
      }

      if (attempt < attempts) {
        const delayMs = baseDelayMs * attempt;
        logAuthDebug('fetch_profile_retry_wait', { userId, attempt, delayMs });
        await wait(delayMs);
      }
    }

    logAuthDebug('fetch_profile_retry_exhausted', { userId, attempts });
    return null;
    },
    [fetchProfile]
  );

  useEffect(() => {
    let isMounted = true;
    logAuthDebug('profile_effect_mount');

    const initializeUser = async ({ silent = false, userHint = null, sessionHint = null } = {}) => {
      const seq = ++initSeqRef.current;
      logAuthDebug('initialize_user_start', {
        silent,
        seq,
        hasUserHint: Boolean(userHint?.id),
        hasSessionHint: Boolean(sessionHint?.access_token),
      });
      if (!silent) setLoading(true);

      try {
        // Fast path: when Supabase already provides the session user via auth events,
        // use it immediately instead of waiting for an extra network validation call.
        let currentUser = userHint?.id ? userHint : null;
        let authStatus = currentUser ? 'ok' : 'none';

        if (!currentUser) {
          const authResult = await ensureAuthenticated();
          currentUser = authResult?.user || null;
          authStatus = authResult?.status || 'none';
        }

        logAuthDebug('initialize_user_after_auth', {
          hasUser: Boolean(currentUser),
          status: authStatus,
          seq,
        });

        if (!isMounted || initSeqRef.current !== seq) return;

        if (currentUser) {
          // Set auth UI state as soon as auth is confirmed.
          setUser(currentUser);
          currentUserIdRef.current = currentUser.id;
          const cachedPoints = readCachedPoints(currentUser.id);
          if (cachedPoints) {
            setSeasonPoints(cachedPoints.seasonPoints);
            setLifetimePoints(cachedPoints.lifetimePoints);
            setCheckinStreak(cachedPoints.checkinStreak);
            if (cachedPoints.previousSeason) {
              setPreviousSeason(cachedPoints.previousSeason);
            }
            logAuthDebug('points_cache_applied', {
              seq,
              userId: currentUser.id,
              seasonPoints: cachedPoints.seasonPoints,
              lifetimePoints: cachedPoints.lifetimePoints,
              checkinStreak: cachedPoints.checkinStreak,
              hasPreviousSeason: Boolean(cachedPoints.previousSeason),
            });
          } else {
            logAuthDebug('points_cache_miss', { seq, userId: currentUser.id });
          }
          logAuthDebug('initialize_user_set_user', {
            seq,
            userId: currentUser.id,
            source: userHint?.id ? 'hint' : 'validated',
          });

          // Existing users: one profile query is enough (includes coins + username).
          // Only run profile creation flow when profile row is missing.
          let profileData = await fetchProfileWithRetry(currentUser.id, {
            accessTokenHint: sessionHint?.access_token || null,
          });
          logAuthDebug('initialize_user_profile_lookup', {
            seq,
            userId: currentUser.id,
            hasProfileData: Boolean(profileData),
          });
          if (!profileData) {
            logAuthDebug('initialize_user_profile_init_start', {
              seq,
              userId: currentUser.id,
            });
            const initialized = await initializeProfile(currentUser);
            logAuthDebug('initialize_user_profile_init_result', {
              seq,
              userId: currentUser.id,
              initialized,
            });
            if (initialized) {
              profileData = await fetchProfileWithRetry(currentUser.id, {
                accessTokenHint: sessionHint?.access_token || null,
              });
              logAuthDebug('initialize_user_profile_lookup_after_init', {
                seq,
                userId: currentUser.id,
                hasProfileData: Boolean(profileData),
              });
            }
          }

          // Hint-based bootstraps (from auth events) can race with token hydration on
          // hard refresh. If we still don't have a profile, re-validate auth and retry.
          if (!profileData && userHint?.id) {
            logAuthDebug('initialize_user_hint_retry_start', {
              seq,
              hintedUserId: userHint.id,
            });
            const verifiedAuth = await ensureAuthenticated();
            const verifiedUser = verifiedAuth?.user || null;
            logAuthDebug('initialize_user_hint_retry_auth', {
              seq,
              hasVerifiedUser: Boolean(verifiedUser?.id),
              verifiedUserId: verifiedUser?.id || null,
              status: verifiedAuth?.status || 'none',
            });
            if (verifiedUser?.id) {
              currentUser = verifiedUser;
              setUser(verifiedUser);
              currentUserIdRef.current = verifiedUser.id;
              logAuthDebug('initialize_user_hint_retry_set_user', {
                seq,
                userId: verifiedUser.id,
              });

              profileData = await fetchProfileWithRetry(verifiedUser.id, {
                accessTokenHint: sessionHint?.access_token || null,
              });
              logAuthDebug('initialize_user_hint_retry_profile_lookup', {
                seq,
                userId: verifiedUser.id,
                hasProfileData: Boolean(profileData),
              });
              if (!profileData) {
                const initialized = await initializeProfile(verifiedUser);
                logAuthDebug('initialize_user_hint_retry_profile_init_result', {
                  seq,
                  userId: verifiedUser.id,
                  initialized,
                });
                if (initialized) {
                  profileData = await fetchProfileWithRetry(verifiedUser.id, {
                    accessTokenHint: sessionHint?.access_token || null,
                  });
                  logAuthDebug('initialize_user_hint_retry_profile_lookup_after_init', {
                    seq,
                    userId: verifiedUser.id,
                    hasProfileData: Boolean(profileData),
                  });
                }
              }
            }
          }

          if (!isMounted || initSeqRef.current !== seq) return;
          setProfile(profileData);
          setCoins(Number.isFinite(profileData?.coins) ? profileData.coins : 0);
          logAuthDebug('initialize_user_apply_profile', {
            seq,
            userId: currentUser.id,
            hasProfile: Boolean(profileData),
            username: profileData?.username || null,
            coins: profileData?.coins ?? null,
          });
        } else if (authStatus === 'unknown') {
          // Avoid clearing local state when auth is temporarily unreachable.
          logAuthDebug('initialize_user_auth_unknown_keep_state', { seq });
          return;
        } else {
          setUser(null);
          setProfile(null);
          setCoins(0);
          setSeasonPoints(0);
          setLifetimePoints(0);
          setCheckinStreak(0);
          setPreviousSeason(null);
          setDailyCheckinReward(null);
          logAuthDebug('initialize_user_clear_state', { seq, status: authStatus });
        }
      } catch (error) {
        logAuthDebug('initialize_user_error', { seq, message: error?.message || String(error) });
        console.error('Error initializing user/profile:', error);
      } finally {
        // Important: don't let the UI get stuck in Loading... due to mid-flight auth events.
        // A common startup race is:
        // - initializeUser(silent:false) sets loading=true
        // - Supabase emits TOKEN_REFRESHED and we call initializeUser(silent:true), which bumps seq
        // - the first call's finally won't run (seq mismatch), and the silent call won't clear loading
        // Always clear loading for the latest init sequence; silent only controls whether we set loading=true.
        if (isMounted && initSeqRef.current === seq) {
          setLoading(false);
        }
        logAuthDebug('initialize_user_end', { silent, seq });
      }
    };

    initializeUser({ silent: false });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        logAuthDebug('auth_state_change', { event, session });
        
        // Only clear state on explicit sign out.
        if (event === 'SIGNED_OUT') {
          setUser(null);
          currentUserIdRef.current = null;
          setProfile(null);
          setCoins(0);
          setSeasonPoints(0);
          setLifetimePoints(0);
          setCheckinStreak(0);
          setPreviousSeason(null);
          setDailyCheckinReward(null);
          setLoading(false);
          logAuthDebug('auth_state_signed_out_cleared');
          return;
        }

        // If we get a non-signout event without a session, keep current state.
        if (!session?.user) {
          logAuthDebug('auth_state_no_session', { event });
          return;
        }

        const hadExistingUser = Boolean(currentUserIdRef.current);

        // Make signed-in UI available immediately from local session data.
        setUser((prevUser) => {
          if (prevUser?.id === session.user.id) return prevUser;
          return session.user;
        });
        currentUserIdRef.current = session.user.id;

        // INITIAL_SESSION fires on page load/refresh with the stored session.
        // We need to handle it to properly restore the user's auth state.
        if (
          event === 'INITIAL_SESSION' ||
          event === 'SIGNED_IN' ||
          event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED'
        ) {
          // UX: avoid greying out the UI on tab focus/visibility changes.
          // Supabase may emit SIGNED_IN / USER_UPDATED / INITIAL_SESSION-like events
          // during session revalidation. If we already have a user in state, run the
          // refresh silently (don't flip loading=true), but still re-fetch profile/coins.
          const silent =
            event === 'TOKEN_REFRESHED' ||
            (hadExistingUser &&
              (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'USER_UPDATED'));
          const hintedUser = event === 'INITIAL_SESSION' ? null : session.user;
          logAuthDebug('auth_state_reinitialize', {
            event,
            silent,
            hasExistingUser: hadExistingUser,
            hintedUserId: hintedUser?.id || null,
          });
          await initializeUser({ silent, userHint: hintedUser, sessionHint: session });
        }
      }
    );

    return () => {
      isMounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [fetchProfileWithRetry]);

  // Refresh coin balance
  const refreshCoins = useCallback(async () => {
    const coinBalance = await getCoins(user);
    setCoins(coinBalance);
    return coinBalance;
  }, [user]);

  // Add coins and update state
  const addUserCoins = async (amount) => {
    const success = await addCoins(amount);
    if (success) {
      await refreshCoins();
    }
    return success;
  };

  // Spend coins and update state
  const spendUserCoins = async (amount) => {
    const success = await spendCoins(amount);
    if (success) {
      await refreshCoins();
    }
    return success;
  };

  // Handle auto-checkin result — updates season points + streak
  const handleCheckinResult = useCallback((result) => {
    if (!result) return;
    const nextSeasonPoints = result.seasonPoints ?? 0;
    const nextLifetimePoints = result.lifetimePoints ?? 0;
    const nextCheckinStreak = result.checkinStreak ?? 0;
    const checkinPointsAwarded = Math.max(0, Number(result?.pointsAwarded) || 0);
    const streakMilestone = Number.isFinite(Number(result?.streakMilestone))
      ? Number(result.streakMilestone)
      : null;
    const alreadyCheckedIn = Boolean(result?.alreadyCheckedIn);
    const referralPayoutProcessed = Boolean(result?.referralPayout?.processed);

    setSeasonPoints(nextSeasonPoints);
    setLifetimePoints(nextLifetimePoints);
    setCheckinStreak(nextCheckinStreak);
    if (result.previousSeason) {
      setPreviousSeason(result.previousSeason);
    }

    if (user?.id) {
      writeCachedPoints(user.id, {
        seasonPoints: nextSeasonPoints,
        lifetimePoints: nextLifetimePoints,
        checkinStreak: nextCheckinStreak,
        previousSeason: result.previousSeason ?? null,
      });
      logAuthDebug('points_cache_written', {
        userId: user.id,
        seasonPoints: nextSeasonPoints,
        lifetimePoints: nextLifetimePoints,
        checkinStreak: nextCheckinStreak,
        hasPreviousSeason: Boolean(result.previousSeason),
      });
    }

    if (!alreadyCheckedIn && checkinPointsAwarded > 0) {
      setDailyCheckinReward({
        pointsAwarded: checkinPointsAwarded,
        checkinStreak: nextCheckinStreak,
        streakMilestone,
      });
    }

    // Referral bonus coins can be awarded during check-in.
    // Refresh coin balance immediately so users don't need a page reload.
    if (referralPayoutProcessed) {
      refreshCoins()
        .then((coinBalance) => {
          logAuthDebug('auto_checkin_referral_coin_refresh', {
            userId: user?.id || null,
            coins: coinBalance,
          });
        })
        .catch((err) => {
          logAuthDebug('auto_checkin_referral_coin_refresh_error', {
            userId: user?.id || null,
            message: err?.message || String(err),
          });
        });
    }
  }, [refreshCoins, user?.id]);

  // Fire daily check-in on mount (fire-and-forget)
  useAutoCheckin(user?.id ?? null, handleCheckinResult);

  const dismissDailyCheckinReward = useCallback(() => {
    setDailyCheckinReward(null);
  }, []);

  // Add this new function
  const updateUsername = async (newUsername) => {
    if (!user) return { success: false, error: "Not authenticated" };

    const { isValid, normalizedName, error: validationError } = validatePlayerName(newUsername);
    if (!isValid) {
      return { success: false, error: validationError };
    }
    
    try {
      // Update username in Supabase
      const { error } = await supabase
        .from('profiles')
        .update({ username: normalizedName })
        .eq('id', user.id);
        
      if (error) throw error;
      
      // Refresh profile data
      const updatedProfile = await fetchProfile(user.id);
      setProfile(updatedProfile);
      
      return { success: true };
    } catch (error) {
      console.error('Error updating username:', error);
      return { success: false, error: error.message };
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        user,
        profile,
        coins,
        loading,
        refreshCoins,
        addCoins: addUserCoins,
        spendCoins: spendUserCoins,
        updateUsername,
        seasonPoints,
        setSeasonPoints,
        lifetimePoints,
        setLifetimePoints,
        checkinStreak,
        previousSeason,
        setPreviousSeason,
        dailyCheckinReward,
        dismissDailyCheckinReward,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext); 
